import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { DestroyRef, Injectable, NgZone, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActiveToast, ToastrService } from 'ngx-toastr';
import {
  EMPTY,
  catchError,
  filter,
  firstValueFrom,
  fromEvent,
  interval,
  switchMap,
  take
} from 'rxjs';

import { environment } from '../../../environments/environment';
import { SKIP_AUTH_INTERCEPTOR } from '../auth/http-context-tokens';
import { AppVersionInfo } from '../models/app-version.model';

@Injectable({
  providedIn: 'root'
})
export class AppVersionService {
  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly toastr = inject(ToastrService);
  private readonly ngZone = inject(NgZone);

  private currentBuildId: string | null = null;
  private updatePromptShown = false;
  private countdownIntervalId: ReturnType<typeof setInterval> | null = null;

  async init(): Promise<void> {
    const config = environment.appVersionCheck;

    if (!config?.enabled) {
      return;
    }

    this.currentBuildId = this.getCompiledBuildId();

    if (!this.currentBuildId) {
      const initialVersion = await this.fetchVersionInfo();
      this.currentBuildId = initialVersion?.buildId ?? null;
    }

    this.startPolling(config.pollIntervalMs);
    this.startVisibilityCheck();
    this.checkForUpdate();
  }

  private getCompiledBuildId(): string | null {
    if (typeof APP_BUILD_ID === 'string' && APP_BUILD_ID.length > 0) {
      return APP_BUILD_ID;
    }

    return null;
  }

  private startPolling(pollIntervalMs: number): void {
    interval(pollIntervalMs)
      .pipe(
        switchMap(() => this.fetchVersionInfo$()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((versionInfo) => {
        if (versionInfo) {
          this.handleVersionCheck(versionInfo);
        }
      });
  }

  private startVisibilityCheck(): void {
    fromEvent(this.document, 'visibilitychange')
      .pipe(
        filter(() => this.document.visibilityState === 'visible'),
        switchMap(() => this.fetchVersionInfo$()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((versionInfo) => {
        if (versionInfo) {
          this.handleVersionCheck(versionInfo);
        }
      });
  }

  private checkForUpdate(): void {
    this.fetchVersionInfo$().subscribe((versionInfo) => {
      if (versionInfo) {
        this.handleVersionCheck(versionInfo);
      }
    });
  }

  private handleVersionCheck(remoteVersion: AppVersionInfo): void {
    if (!this.currentBuildId) {
      this.currentBuildId = remoteVersion.buildId;
      return;
    }

    if (
      remoteVersion.buildId === this.currentBuildId ||
      this.updatePromptShown
    ) {
      return;
    }

    this.updatePromptShown = true;
    this.ngZone.run(() => {
      this.showUpdateNotification(environment.appVersionCheck.reloadCountdownSeconds);
    });
  }

  private showUpdateNotification(countdownSeconds: number): void {
    let remaining = countdownSeconds;

    const toast = this.toastr.info(
      this.buildUpdateMessage(remaining),
      'Nova versão disponível',
      {
        disableTimeOut: true,
        closeButton: false,
        tapToDismiss: false,
        enableHtml: true,
        toastClass: 'toast-app-update ngx-toastr',
        positionClass: 'toast-top-middle'
      }
    );

    this.bindUpdateButtonHandler(toast);

    this.countdownIntervalId = setInterval(() => {
      this.ngZone.run(() => {
        remaining -= 1;
        this.updateCountdownDisplay(remaining);

        if (remaining <= 0) {
          this.clearCountdown();
          this.reloadApplication();
        }
      });
    }, 1000);
  }

  private bindUpdateButtonHandler(toast: ActiveToast<unknown>): void {
    toast.onShown.pipe(take(1)).subscribe(() => {
      setTimeout(() => {
        const toastEl = this.document.querySelector('.toast-app-update');
        if (!toastEl || toastEl.getAttribute('data-update-bound') === 'true') {
          return;
        }

        toastEl.setAttribute('data-update-bound', 'true');
        toastEl.addEventListener('click', (event) => {
          const target = event.target as HTMLElement;
          if (!target.closest('.conviva-app-update-btn')) {
            return;
          }

          event.preventDefault();
          event.stopPropagation();
          this.ngZone.run(() => this.reloadApplication());
        });
      });
    });
  }

  private updateCountdownDisplay(remainingSeconds: number): void {
    const countdownEl = this.document.querySelector(
      '.toast-app-update .conviva-app-update-countdown'
    );

    if (countdownEl) {
      countdownEl.textContent = String(remainingSeconds);
    }
  }

  private buildUpdateMessage(remainingSeconds: number): string {
    return `
      <p class="mb-2">
        Uma nova versão do sistema está disponível. O sistema será atualizado em
        <strong class="conviva-app-update-countdown">${remainingSeconds}</strong> segundos.
      </p>
      <button type="button" class="btn btn-sm btn-light conviva-app-update-btn">
        Atualizar agora
      </button>
    `;
  }

  private reloadApplication(): void {
    this.clearCountdown();
    this.document.defaultView?.location.reload();
  }

  private clearCountdown(): void {
    if (this.countdownIntervalId !== null) {
      clearInterval(this.countdownIntervalId);
      this.countdownIntervalId = null;
    }
  }

  private fetchVersionInfo$() {
    return this.http
      .get<AppVersionInfo>(this.getVersionUrl(), {
        headers: new HttpHeaders({ 'Cache-Control': 'no-cache' }),
        context: new HttpContext().set(SKIP_AUTH_INTERCEPTOR, true)
      })
      .pipe(catchError(() => EMPTY));
  }

  private async fetchVersionInfo(): Promise<AppVersionInfo | null> {
    try {
      return await firstValueFrom(this.fetchVersionInfo$());
    } catch {
      return null;
    }
  }

  private getVersionUrl(): string {
    const baseHref = this.document.querySelector('base')?.href ?? '/';
    const baseUrl = new URL(baseHref, this.document.defaultView?.location.origin ?? '');
    return `${baseUrl.origin}${baseUrl.pathname.replace(/\/$/, '')}/version.json?t=${Date.now()}`;
  }
}
